/**
 * FP3-044 Phase A — Admin Goals tab (read-only).
 *
 * Groups goals by provider and surfaces status / trend / last-data
 * recency. No goal authoring. Each goal links into the provider's
 * read-only workspace shadow.
 */
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";

const STATUS_STYLE = {
  active: "bg-[#E8F2EE] text-[#2F6B53]",
  achieved: "bg-[#EEE9F7] text-[#5A4B85]",
  archived: "bg-[#F3F1EC] text-[#6B7280]",
  draft: "bg-[#FDF8F3] text-[#9C6B2E]",
};

const TREND_LABEL = {
  improving: "↗ improving",
  declining: "↘ declining",
  plateauing: "→ plateauing",
  new: "• new",
};

export default function AdminGoalsTab({ studentId, goalSummary = [], team = [], compactPreview = false }) {
  const teamById = Object.fromEntries(team.map((t) => [t.provider_id, t]));
  const rowsToShow = compactPreview ? goalSummary.slice(0, 2) : goalSummary;

  return (
    <Card className="p-5 bg-white border-[#E5E3DB]" data-testid="admin-goals-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[13px] font-medium text-[#1F2937]">Goals & progress</p>
      </div>
      {goalSummary.length === 0 ? (
        <p className="text-[12px] text-[#6B7280] italic" data-testid="admin-goals-empty">
          No goals yet across the team.
        </p>
      ) : (
        <div className="space-y-4">
          {rowsToShow.map((p) => {
            const prov = teamById[p.provider_id];
            return (
              <div
                key={p.provider_id}
                data-testid={`admin-goals-provider-${p.provider_id}`}
                className="border border-[#E5E3DB] rounded-lg p-3"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-[12px] font-medium text-[#1F2937]">
                    {prov ? `${prov.provider_first_name} ${prov.provider_last_name}` : p.provider_id}
                    {prov?.provider_role && (
                      <span className="text-[10px] text-[#6B7280] ml-2">· {prov.provider_role}</span>
                    )}
                  </p>
                  <p className="text-[11px] text-[#6B7280]">
                    {p.active} active · {p.achieved} achieved
                    {p.no_recent_data_count > 0 && (
                      <span className="text-[#9C6B2E] font-medium ml-2">
                        · {p.no_recent_data_count} no recent data
                      </span>
                    )}
                  </p>
                </div>

                <div className="mt-2 space-y-1.5">
                  {(p.goals || []).map((g) => (
                    <Link
                      key={g.id}
                      to={`/students/${studentId}?as_provider=${p.provider_id}&from_admin_overview=1`}
                      data-testid={`admin-goal-row-${g.id}`}
                      className="flex items-center gap-2 text-[12px] hover:bg-[#F3F1EC] px-2 py-1.5 rounded-md"
                    >
                      <span
                        className={`text-[9.5px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${STATUS_STYLE[g.status || "active"]}`}
                      >
                        {g.status || "active"}
                      </span>
                      <span className="flex-1 min-w-0 truncate text-[#1F2937]">
                        {g.label || g.category || "(no label)"}
                      </span>
                      <span className="text-[10.5px] text-[#6B7280] tabular-nums">
                        {(g.current_percentage ?? 0)}% / {g.target_percentage ?? "—"}%
                      </span>
                      {g.trend && TREND_LABEL[g.trend] && (
                        <span className="text-[10.5px] text-[#6B7280]">
                          {TREND_LABEL[g.trend]}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
          {compactPreview && goalSummary.length > rowsToShow.length && (
            <p className="text-[11px] text-[#6B7280]">
              Showing 2 of {goalSummary.length} providers. Open the Goals tab for the full picture.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
